/**
 * 주소 → 화면 머리(사이드바 구역 이름 · 메뉴 이름). 사이드바가 그리는 목록(`getNavData`)을 그대로 읽는다 —
 * 메뉴 이름을 바꾸면 화면 머리도 같이 바뀐다.
 */

import { paths } from 'src/routes/paths';

/** 화면 머리 두 줄 — `section` 은 사이드바 구역 표제(`subheader`) 그대로, 구역 밖 줄이면 null */
export type NavHeading = { section: string | null; title: string };

/** 서비스 뿌리 — 홈 줄의 주소. 이 주소만은 하위 주소를 덮지 않는다(`isWithin`) */
const SERVICE_ROOT = paths.service.root;

type NavItemLike = { title?: unknown; path?: unknown; children?: unknown };
type NavGroupLike = { subheader?: unknown; items?: unknown };

/** 사이드바 밖에서 여는 화면(설정 등) — 구역 없이 이름만 선다 */
export type NavExtraEntry = { title: string; path: string };

/**
 * 지금 주소가 어느 메뉴 줄의 화면인지 고른다.
 *
 * 🔑 **가장 긴 주소가 이긴다** — `/service/tracking/target-mentions/<id>` 는 그 세트 줄, 하위 주소
 *   (`/service/discover/brands?brand=…` · `/service/discover/bookmarks/<id>` · `/service/operations/seeding/<id>`)는 부모 줄이다.
 * ⚠️ 비교는 경로만 한다(질의 `?…` 는 버린다) — 같은 경로면 먼저 선 줄(부모)이 이긴다.
 * ⚠️ 앞이 겹치는 주소는 `/` 경계로만 잇는다.
 * 🚨 홈(서비스 뿌리 `/service`)은 **경로가 같을 때만** 맞는다 — 모든 서비스 주소가 그 아래라, 접두로 재면
 *   메뉴 밖 화면이 전부 「홈」 머리를 단다.
 * @param groups `getNavData` 가 돌려준 구역 목록
 * @param pathname 지금 주소의 경로
 * @param extras 사이드바에 없는 화면 — 구역 없이 이름만 돌려준다
 * @param fallbackGroups 숨김 전 전체 메뉴(`getFullNavData`) — 위 둘에 맞는 줄이 없을 때만 본다
 *   (플랜·labs 로 사이드바에서 빠진 화면을 주소로 열었을 때)
 * @returns 맞는 줄이 없으면 null(공유 페이지·메뉴 밖 화면)
 */
export function resolveNavHeading(
  groups: readonly unknown[] | null | undefined,
  pathname: string | null | undefined,
  extras: readonly NavExtraEntry[] = [],
  fallbackGroups: readonly unknown[] | null = null,
): NavHeading | null {
  const found = pickHeading(groups, pathname, extras);
  if (found || !fallbackGroups) return found;
  return pickHeading(fallbackGroups, pathname, []);
}

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

function pickHeading(
  groups: readonly unknown[] | null | undefined,
  pathname: string | null | undefined,
  extras: readonly NavExtraEntry[],
): NavHeading | null {
  const current = normalizePath(pathname);
  if (!current) return null;

  const matches: (NavHeading & { length: number })[] = [];
  const consider = (title: unknown, path: unknown, section: string | null) => {
    if (typeof title !== 'string' || !title.trim() || typeof path !== 'string') return;
    const target = normalizePath(path);
    if (!target || !isWithin(current, target)) return;
    matches.push({ section, title, length: target.length });
  };

  (groups ?? []).forEach((rawGroup) => {
    const group = (rawGroup ?? {}) as NavGroupLike;
    const section = typeof group.subheader === 'string' && group.subheader.trim() ? group.subheader : null;
    asArray(group.items).forEach((rawItem) => {
      const item = (rawItem ?? {}) as NavItemLike;
      consider(item.title, item.path, section);
      asArray(item.children).forEach((rawChild) => {
        const child = (rawChild ?? {}) as NavItemLike;
        consider(child.title, child.path, section);
      });
    });
  });

  extras.forEach((entry) => consider(entry.title, entry.path, null));

  const best = matches.reduce<(typeof matches)[number] | null>(
    (picked, match) => (picked && picked.length >= match.length ? picked : match),
    null,
  );
  return best ? { section: best.section, title: best.title } : null;
}

/** 질의·조각을 버리고 끝의 `/` 를 떼어 낸 경로 — 루트 `/` 는 남는다 */
function normalizePath(path: string | null | undefined): string {
  const bare = String(path ?? '').split(/[?#]/)[0].trim();
  if (!bare) return '';
  return bare.length > 1 ? bare.replace(/\/+$/, '') : bare;
}

function isWithin(current: string, target: string): boolean {
  if (target === SERVICE_ROOT) return current === target;
  return current === target || current.startsWith(`${target}/`);
}
